import React, { useEffect, useState } from 'react';
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import axios from "axios";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Layers,
    Plus,
    X,
    TrendingUp,
    AlertTriangle,
    Clock,
    Calendar,
    BarChart3,
    Loader2,
} from "lucide-react";

import { BACKEND_API_URL } from "../utils/api";
const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

// Key configuration with icons and colors
const KEY_CONFIG = {
    priority: {
        label: "Priority",
        icon: TrendingUp,
        color: "text-amber-600",
        bg: "bg-amber-500/10",
        border: "border-amber-500/25",
    },
    risk: {
        label: "Risk",
        icon: AlertTriangle,
        color: "text-rose-600",
        bg: "bg-rose-500/10",
        border: "border-rose-500/25",
    },
    time: {
        label: "Time in Hrs",
        icon: Clock,
        color: "text-sky-600",
        bg: "bg-sky-500/10",
        border: "border-sky-500/25",
    },
    eta: {
        label: "ETA",
        icon: Calendar,
        color: "text-violet-600",
        bg: "bg-violet-500/10",
        border: "border-violet-500/25",
    },
};

const AggregatorView = ({ folderId, data, onDataChange }) => {
    const [key, setKey] = useState("");
    const [value, setValue] = useState("");
    const [summary, setSummary] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSummaryLoading, setIsSummaryLoading] = useState(true);

    useEffect(() => {
        getAggregatorSummary();
    }, []);

    const getAggregatorSummary = async () => {
        setIsSummaryLoading(true);
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/aggregator/summary/${folderId}`, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            setSummary(response.data);
        } catch (error) {
            if (error?.response?.data?.message) {
                toast.error("Error - " + error?.response?.data?.message);
            } else {
                toast.error("Aggregator Summary Retrieval Failed!");
            }
        } finally {
            setIsSummaryLoading(false);
        }
    };

    const onAddNewKeyPair = async () => {
        if (!key || !value) {
            toast.error("Please select a key and enter a value");
            return;
        }
        setIsLoading(true);
        try {
            const accessToken = Cookies.get('access_token');
            const payload = { "key": key, "value": value };
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/aggregator/add/${folderId}`, payload, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            onDataChange(response.data);
            setKey("");
            setValue("");
            toast.success("Added successfully");
            // Refresh summary after adding
            getAggregatorSummary();
        } catch (error) {
            if (error?.response?.data?.message) {
                toast.error("Error - " + error?.response?.data?.message);
            } else {
                toast.error("Aggregator Data addition failed!");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getKeyConfig = (keyName) => {
        return KEY_CONFIG[keyName] || {
            label: keyName,
            icon: Layers,
            color: "text-slate-600",
            bg: "bg-slate-500/10",
            border: "border-slate-500/25",
        };
    };

    return (
        <div className="space-y-6">
            {/* Aggregator Keys Section */}
            <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center size-8 rounded-lg bg-indigo-500/10">
                        <Layers className="size-4 text-indigo-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">Aggregator Keys</h3>
                        <p className="text-xs text-muted-foreground">Add key-value pairs to track metrics</p>
                    </div>
                </div>

                {/* Add New Key-Value Form */}
                <div className="flex flex-wrap items-end gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex-1 min-w-[140px] space-y-1.5">
                        <Label htmlFor="key-select" className="text-xs text-muted-foreground">
                            Key
                        </Label>
                        <Select value={key} onValueChange={setKey}>
                            <SelectTrigger id="key-select" className="h-9 w-full">
                                <SelectValue placeholder="Select key..." />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(KEY_CONFIG).map(([keyValue, config]) => {
                                    const Icon = config.icon;
                                    return (
                                        <SelectItem key={keyValue} value={keyValue}>
                                            <div className="flex items-center gap-2">
                                                <Icon className={cn("size-3.5", config.color)} />
                                                <span>{config.label}</span>
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex-1 min-w-[140px] space-y-1.5">
                        <Label htmlFor="value-input" className="text-xs text-muted-foreground">
                            Value
                        </Label>
                        <Input
                            id="value-input"
                            type="text"
                            placeholder="Enter value..."
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            className="h-9"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    onAddNewKeyPair();
                                }
                            }}
                        />
                    </div>

                    <Button
                        onClick={onAddNewKeyPair}
                        disabled={isLoading || !key || !value}
                        size="sm"
                        className="h-9 gap-1.5"
                    >
                        {isLoading ? (
                            <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                            <Plus className="size-3.5" />
                        )}
                        Add
                    </Button>
                </div>

                {/* Key-Value Badges */}
                <div className="min-h-[40px]">
                    {data && data.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {data.map(({ id, key: keyName, value: keyValue }) => {
                                const config = getKeyConfig(keyName);
                                const Icon = config.icon;
                                return (
                                    <Tooltip key={id}>
                                        <TooltipTrigger asChild>
                                            <Badge
                                                variant="outline"
                                                className={cn(
                                                    "h-7 px-2.5 gap-1.5 cursor-default transition-colors",
                                                    config.bg,
                                                    config.border,
                                                    config.color
                                                )}
                                            >
                                                <Icon className="size-3" />
                                                <span className="font-medium">{config.label}</span>
                                                <span className="text-foreground/70">→</span>
                                                <span className="font-semibold text-foreground">{keyValue}</span>
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">
                                            <p>{config.label}: {keyValue}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-[40px] rounded-lg border border-dashed border-border/60 bg-muted/30">
                            <p className="text-xs text-muted-foreground">No aggregator keys added yet</p>
                        </div>
                    )}
                </div>
            </div>

            <Separator />

            {/* Aggregator Summary Section */}
            <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center size-8 rounded-lg bg-emerald-500/10">
                        <BarChart3 className="size-4 text-emerald-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">Aggregator Summary</h3>
                        <p className="text-xs text-muted-foreground">Calculated metrics overview</p>
                    </div>
                </div>

                <div className="min-h-[52px]">
                    {isSummaryLoading ? (
                        <div className="flex items-center justify-center h-[52px] rounded-lg border border-dashed border-border/60 bg-muted/30">
                            <Loader2 className="size-4 animate-spin text-muted-foreground" />
                        </div>
                    ) : Object.keys(summary).length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {Object.entries(summary).map(([summaryKey, summaryValue]) => {
                                const config = getKeyConfig(summaryKey);
                                const Icon = config.icon;
                                return (
                                    <div
                                        key={summaryKey}
                                        className={cn(
                                            "flex items-center gap-2 p-3 rounded-lg border transition-colors",
                                            config.bg,
                                            config.border,
                                            "hover:bg-opacity-20"
                                        )}
                                    >
                                        <div className={cn(
                                            "flex items-center justify-center size-7 rounded-md",
                                            config.bg
                                        )}>
                                            <Icon className={cn("size-3.5", config.color)} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium truncate">
                                                {config.label}
                                            </p>
                                            <p className="text-sm font-bold text-foreground truncate">
                                                {summaryValue !== null ? summaryValue : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-[52px] rounded-lg border border-dashed border-border/60 bg-muted/30">
                            <p className="text-xs text-muted-foreground">No summary data available</p>
                        </div>
                    )}
                </div>
            </div>

            <Separator className="mt-2" />
        </div>
    );
};

export default AggregatorView;
